import {ResourceType} from 'common/report/constants';
import {Literal} from 'common/widgets/cohorts-query-builder/models/builder/literal';
import {
  isCohortLiteral,
  isEventLiteral,
  isPeopleLiteral,
} from 'common/widgets/cohorts-query-builder/util/staged-cohort';
import {ALL_USERS_COHORT} from 'common/widgets/query-block/properties-menu/constants';
import {
  CohortGroupEntry,
  CohortItem,
  FiltersOperator,
  SelectorTypeDefinitions,
} from 'common/widgets/query-entry-section/types';
import {ClausePosition, StagedCohort} from 'common/widgets/cohorts-query-builder/models/builder/staged-cohort';
import {CohortSegment, isInlineCohortSegment} from 'common/widgets/query-block/segment-menu/types';
import {PeopleLiteral} from 'common/widgets/cohorts-query-builder/models/builder/people-literal';
import {PeoplePropertyClause} from 'common/widgets/cohorts-query-builder/models/builder/people-property-clause';
import {EventLiteral} from 'common/widgets/cohorts-query-builder/models/builder/event-literal';
import {EventFrequencyClause} from 'common/widgets/cohorts-query-builder/models/builder/event-frequency-clause';
import {LogicalOperator, PredefinedCohort} from 'common/widgets/cohorts-query-builder/models/enums/expression-enums';

import {
  applyBehaviorFilterToStagedCohort,
  applyCommonPropertyFiltersToStagedCohort,
  convertCommonPropertyFilterToCohortsPropertyFilter,
  convertPropertyFilterToCommon,
  createCohortCommonPropertyFilter,
  getEventFiltersFromEventsFrequencyClause,
  getEventFiltersFromPeoplePropertyClause,
  getFiltersOperatorFromLiteralClauseOperators,
  getFiltersOperatorFromPropertyFilterOperator,
  getGroupingOperatorFromLogicalOperators,
  getLiteralClauseOperatorsFromFiltersOperator,
} from './filters';

export function getLiteralFromEntry(entry: CohortGroupEntry): Literal {
  let stagedCohort = new StagedCohort();

  // Check if the overral format is valid
  if (entry.event?.value !== ALL_USERS_COHORT.value) {
    throw new Error(`Invalid Entry format for conversion to a Literal, expected All People top level event`);
  }

  if (!entry.filters.length) {
    return stagedCohort.literals[0];
  }

  const [firstFilter, ...additionalFilters] = entry.filters;
  const clausePosition: ClausePosition = {clauseIdx: 0, literalIdx: 0};

  // Check if entry can be mapped to an eventFrequencyClause
  if (firstFilter?.customProperty) {
    /** [shawn] The default StagedCohort clause is a cohort and not an event frequency, we swap it manually to apply changes
     * also order of setting the clauseOperators matters, as funnel literals with a then/null operator have a special behavior
     * https://github.com/mixpanel/analytics/blob/112d57d63c523eb79e5e2b2bad9a0b1fa423ebbf/iron/common/widgets/cohorts-query-builder/models/builder/event-literal.ts#L140
     */
    stagedCohort = stagedCohort.replaceLiteral(
      new EventLiteral({
        clauses: [EventFrequencyClause.createDefault()],
        labelPrefix: stagedCohort.getNextLabelPrefix(0),
        clauseOperators: getLiteralClauseOperatorsFromFiltersOperator(entry.filtersOperator),
      }),
      clausePosition.literalIdx,
    );

    entry.filters.forEach((filter, i) => {
      stagedCohort = applyBehaviorFilterToStagedCohort(filter, {...clausePosition, clauseIdx: i}, stagedCohort);

      if (i < entry.filters.length - 1) {
        stagedCohort = stagedCohort.addClauseToLiteralAtIndex(clausePosition.literalIdx);
      }
    });

    // TODO: Investigate where clauseOperators get reset in applyBehaviorFilterToStagedCohort to prevent reapplying
    stagedCohort = stagedCohort.updateClauseOperators(
      getLiteralClauseOperatorsFromFiltersOperator(entry.filtersOperator),
      clausePosition.literalIdx,
    );
  } else if (Array.isArray(firstFilter.filterValue) && (firstFilter.filterValue[0] as CohortSegment)?.cohort) {
    const cohortSegment = firstFilter.filterValue[0] as CohortSegment;

    if (isInlineCohortSegment(cohortSegment)) {
      throw new Error(`Inline cohorts are not supported within cohorts ast`);
    }

    // Apply property filters
    stagedCohort = applyCommonPropertyFiltersToStagedCohort(
      additionalFilters,
      clausePosition,
      entry.filtersOperator,
      stagedCohort,
    );

    const {cohort} = cohortSegment;

    // Cohort literal with single cohort clause
    stagedCohort = stagedCohort.updateCohort(cohort.id, clausePosition);

    if (cohort.negated) {
      stagedCohort = stagedCohort.toggleClauseNegated(clausePosition);
    }
  } else {
    // Apply property filters as clauses
    stagedCohort = stagedCohort.replaceLiteral(
      new PeopleLiteral({
        clauses: entry.filters.map(
          (filter) =>
            new PeoplePropertyClause({
              propertyFilters: [convertCommonPropertyFilterToCohortsPropertyFilter(filter)],
            }),
        ),
        labelPrefix: stagedCohort.getNextLabelPrefix(0),
      }),
      clausePosition.literalIdx,
    );

    stagedCohort = stagedCohort.updateClauseOperators(
      getLiteralClauseOperatorsFromFiltersOperator(entry.filtersOperator),
      clausePosition.literalIdx,
    );
  }

  return stagedCohort.literals[0];
}

export const createDefaultCohortMetricEntry = (): CohortGroupEntry => ({
  type: SelectorTypeDefinitions.CohortGroup,
  event: {
    resourceType: ResourceType.Cohort,
    value: ALL_USERS_COHORT.value as string,
    label: ALL_USERS_COHORT.label,
  } as CohortItem,
  filters: [],
  filtersOperator: FiltersOperator.And,
  behavioralFiltersOperator: FiltersOperator.And,
  groupingOperator: FiltersOperator.And,
  property: null,
});

export const COHORT_METRIC_ENTRY_DEFAULT_PROPS = createDefaultCohortMetricEntry();

export function getEntryFromLiteral(
  literal: Literal,
  {
    dataGroupId,
    cohortId,
    groupingOperators = [],
    literalIndex,
  }: {
    literalIndex: number;
    dataGroupId?: string;
    cohortId?: number;
    groupingOperators?: LogicalOperator[];
  },
): CohortGroupEntry | CohortGroupEntry[] {
  const entryDefaultProps = {
    ...createDefaultCohortMetricEntry(),
    dataGroupId: dataGroupId ?? ``,
    groupingOperator: getGroupingOperatorFromLogicalOperators(groupingOperators)[literalIndex] ?? null,
  };

  if ((literal.clauses[0] as EventFrequencyClause).behavior?.isReportBasedCohort()) {
    /** handle report based cohorts by creating a per-line cohort filter */
    return {
      ...entryDefaultProps,
      filters: [createCohortCommonPropertyFilter(cohortId)],
    };
  } else if (isEventLiteral(literal)) {
    return {
      ...entryDefaultProps,
      filters: literal.clauses.map(getEventFiltersFromEventsFrequencyClause).flat(),
      filtersOperator: FiltersOperator.And,
      behavioralFiltersOperator: getFiltersOperatorFromLiteralClauseOperators(literal.clauseOperators),
    };
  } else if (isPeopleLiteral(literal)) {
    return {
      ...entryDefaultProps,
      filters: literal.clauses.map(getEventFiltersFromPeoplePropertyClause).flat(),
      filtersOperator: getFiltersOperatorFromLiteralClauseOperators(literal.clauseOperators),
    };
  } else if (isCohortLiteral(literal)) {
    // Cohort literals only have a single clause
    const [clause] = literal.clauses;
    const isAllUsers = clause.cohortId === PredefinedCohort.AllUsers;
    const clauseFilters = (clause.propertyFilters ?? []).map((filter) =>
      convertPropertyFilterToCommon(filter, clause.negated && isAllUsers),
    );
    const hasFilters = clauseFilters?.length;
    const filtersOperator = getFiltersOperatorFromPropertyFilterOperator(clause.propertyFilterOperator);

    /**
     * a cohort literal may include additional filters, these are ANDed together
     * with the cohort selector, to mimic this same behavior in the old QB
     * we create two ANDed groups which effectively merge both selectors
     */
    const cohortFilterGroup = {
      ...entryDefaultProps,
      filters: isAllUsers
        ? clauseFilters
        : [createCohortCommonPropertyFilter(clause.cohortId as number, clause.negated), ...clauseFilters],
      filtersOperator,
    } as CohortGroupEntry;

    /**
     * for negated cohorts with filters, the old qb behavior is merging the cohort selector
     * together with any filters, and apply a `NOT()` selector around that, we cannot achieve
     * the same thing in the new UI so we map this case to a negated top level cohort group
     */
    if (!isAllUsers && hasFilters && clause.negated && hasFilters) {
      return {
        ...cohortFilterGroup,
        filters: clauseFilters,
        event: {
          resourceType: ResourceType.Cohort,
          label: null,
          negated: true,
          value: clause.cohortId as number,
        },
      };
    } else if (!isAllUsers && hasFilters && filtersOperator === FiltersOperator.Or) {
      /** for cohort literals with OR filters, we create two groups joined together with AND to generate the same selector */
      return [
        {
          ...cohortFilterGroup,
          filters: [createCohortCommonPropertyFilter(clause.cohortId as number, clause.negated)],
          filtersOperator: FiltersOperator.And,
          groupingOperator: FiltersOperator.And,
        },
        {
          ...cohortFilterGroup,
          filters: clauseFilters,
        },
      ];
    } else if (isAllUsers && hasFilters && clause.negated) {
      cohortFilterGroup.filtersOperator =
        filtersOperator === FiltersOperator.And ? FiltersOperator.Or : FiltersOperator.And;
    }

    return cohortFilterGroup;
  }

  throw new Error(`Invalid literal type`);
}
